/**
 * Page values — action layer for "page selection drives a property".
 *
 * Commits the pure model's result (`pageValuesModel.ts`) to the container's
 * Properties and Pages behaviors as ONE undo bundle, shows the state so
 * the viewport shows the page's value, and notifies the Pages panels.
 */

import type { SceneObject } from 'trace-viewer/scene';
import { Pages, Properties } from 'trace-viewer/behaviors';
import type { PropertiesData, PropertyValue } from 'trace-viewer/behaviors';
import { BehaviorCommitCommand, notifyPagesChanged, setDeclarationValue } from 'trace-viewer/editor';
import { clearPageOverride, setPageOverride } from './behaviors/pageValuesModel';
import { pushBundle } from './behaviors/pagesSharedActions';

/**
 * Set (value != null) or clear (value == null) page `pageIndex`'s override of
 * `prop` on `container`. No-op when the container lacks Pages or Properties,
 * or the page index is out of range.
 */
export async function setPageValueOverride(
    container: SceneObject,
    pageIndex: number,
    prop: string,
    value: PropertyValue | null,
): Promise<void> {
    const pages = container.getBehavior(Pages);
    const props = container.getBehavior(Properties);
    if (!pages || !props) return;
    const pageNodes = pages.getPageNodes();
    if (pageIndex < 0 || pageIndex >= pageNodes.length) return;

    const propsBefore = props.getData();
    const pagesBefore = pages.getData();
    const written = value === null
        ? clearPageOverride(propsBefore, pagesBefore.pages, pageIndex, pageNodes[pageIndex].name, pageNodes.length, prop)
        : setPageOverride(propsBefore, pagesBefore.pages, pageIndex, pageNodes[pageIndex].name, pageNodes.length, prop, value);

    const commands: BehaviorCommitCommand[] = [];
    await props.commit(written.props);
    commands.push(new BehaviorCommitCommand(container, 'Properties', propsBefore, props.getData(), null));
    if (JSON.stringify(written.pages) !== JSON.stringify(pagesBefore.pages)) {
        await pages.commit({ ...pagesBefore, pages: written.pages });
        commands.push(new BehaviorCommitCommand(container, 'Pages', pagesBefore, pages.getData(), null));
    }
    pushBundle(value === null ? `Clear ${prop} on ${pageNodes[pageIndex].name}` : `Set ${prop} on ${pageNodes[pageIndex].name}`, commands);

    // If the edited page is showing, show the state the write landed in,
    // which may be a new one, so the viewport reflects the value just written.
    if (pages.getActivePageIndex() === pageIndex) {
        container.getBehavior(Properties)?.setState(written.stateName ?? undefined, true);
    }
    notifyPagesChanged(container);
}

/** Edit the declaration's base value — what every page without an override shows. */
export async function setPageValueBase(container: SceneObject, prop: string, value: PropertyValue): Promise<void> {
    const props = container.getBehavior(Properties);
    if (!props) return;
    const before: PropertiesData = props.getData();
    const next = setDeclarationValue(before, prop, value);
    if (next === before) return;
    await props.commit(next);
    pushBundle(`Set ${prop}`, [new BehaviorCommitCommand(container, 'Properties', before, props.getData(), null)]);
    notifyPagesChanged(container);
}
